import React, {
  ForwardedRef,
  forwardRef,
  ReactNode,
  RefObject,
  MouseEvent,
  useMemo,
  FC,
  isValidElement,
  ReactElement,
} from 'react';
import Badge from '@mui/material/Badge';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import {
  Link,
} from 'react-router-dom';
import LinkIcon from 'core/icons/link';
import {
  useMixpanelTrack,
} from 'core/hooks';
import Image, {
  Props as ImageProps,
} from 'pages/Dashboard/components/Image';
import ToolTip from 'core/components/Tooltip';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  FormattedNamesDTO,
} from 'dtos';
import {
  getDisplayName,
  getInitials,
} from 'pages/Dashboard/utils/helper';
import {
  NameFormatKey,
} from 'types/nameFormat';

type CommonComponentProps = {
  className?: string;
};

type CommonComponentType = FC<CommonComponentProps>;

type LinkProps = {
  to?: string;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  Icon?: CommonComponentType;
};

type Part = 'root' | 'label' | 'nameRoot' | 'link' | 'icon';

type Props = Pick<FormattedNamesDTO, NameFormatKey> & {
  className?: string;
  labelClassName?: string;
  name?: ReactNode | string;
  placeholder?: string;
  children?: ReactNode;
  image?: ReactElement | ImageProps | boolean;
  link?: LinkProps | string;
  classes?: Partial<Record<Part, string>>;
  error?: string;
};

const containerClasses = 'flex gap-2 items-center w-full group/name';

function Name({
  className,
  labelClassName,
  classes,
  name,
  image,
  link,
  error,
  children,
  placeholder = '',
  ...props
}: Props, ref: ForwardedRef<HTMLAnchorElement | HTMLDivElement>) {
  const mixpanelTrack = useMixpanelTrack();

  const displayName = getDisplayName(props, placeholder);
  const resolvedDisplayName = typeof name === 'string'
    ? displayName || name || ''
    : displayName ?? '';
  const labelContent = typeof name !== 'string' && !isNil(name)
    ? name
    : resolvedDisplayName;

  const initials = getInitials(props);

  const isCustomLink = typeof link !== 'string';
  const to = isCustomLink ? link?.to ?? null : link;

  const hasImage = !isNil(image);
  const hasError = notEmptyString(error);
  const icon = isValidElement(image)
    ? image
    : (
      <Image
        alt={resolvedDisplayName ?? ''}
        {...(typeof image === 'object' ? image : { variant: 'circular' })}
      >
        {initials}
      </Image>
    );
  const hasChildren = !isNil(children);
  const isLink = !isNil(link);

  const rootClass = clsx(
    'relative w-full flex items-center gap-2 font-medium',
    className,
    classes?.root,
  );

  const labelClasses = clsx(
    'truncate',
    { 'group-hover/name:text-blue-500': isLink },
    labelClassName,
    classes?.label,
  );

  const IconLink = useMemo(() => (
    (isCustomLink && !isNil(link?.Icon) ? link!.Icon : LinkIcon) as CommonComponentType
  ), [link, isCustomLink]);

  const imageVariant = (image as Record<string, string>)?.variant ?? 'circular';

  return (
    <div className={rootClass}>
      {hasImage && (
        <ToolTip
          title={error}
          hide={!hasError}
        >
          <Badge
            className={clsx({
              'border-4 border-red-600 cursor-pointer': hasError,
              'rounded-lg': imageVariant === 'rounded',
              'rounded-full': imageVariant === 'circular',
            })}
            invisible={!hasError}
            badgeContent="!"
            color="error"
          >
            {icon}
          </Badge>
        </ToolTip>
      )}
      <div className={clsx('flex flex-1 flex-col truncate', classes?.nameRoot)}>
        {isLink
          ? (
            <Link
              ref={ref as RefObject<HTMLAnchorElement>}
              to={to ?? ''}
              className={clsx(
                containerClasses,
                'rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-default-input-focused',
                classes?.link,
              )}
              onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                if (isCustomLink && typeof link?.onClick === 'function') {
                  e.preventDefault();
                  link.onClick(e);
                }
                mixpanelTrack('navigatedToPatientChart', {
                  source: 'patient-link',
                });
              }}
            >
              <h2 className={labelClasses}>
                {labelContent}
              </h2>
              <IconLink
                className={clsx(
                  '!text-sm group-hover/name:!text-blue-500',
                  { '!hidden group-hover/name:!block': isNil(classes?.icon) },
                  classes?.icon,
                )}
              />
            </Link>
          )
          : (
            <div
              ref={ref as RefObject<HTMLDivElement>}
              className={containerClasses}
            >
              <h2 className={labelClasses}>
                {labelContent}
              </h2>
            </div>
          )}
        {hasChildren && children}
      </div>
    </div>
  );
}

export default forwardRef(Name);
